import { createContext, useState, useEffect, PropsWithChildren } from 'react';
import { usePlatform } from './platform-provider';
import { isFullscreenable } from '../types/platform-api';
import { ViewportProps } from '../types/viewport-props';

export const ViewportContext = createContext<ViewportProps | null>(null);

export const ViewportProvider = ({ children }: PropsWithChildren): React.JSX.Element => {
  const { platform } = usePlatform();
  const [state, setState] = useState<ViewportProps>({
    safeAreaInsets: undefined,
    contentSafeAreaInsets: undefined,
  });

  useEffect(() => {
    (async () => {
      if (isFullscreenable(platform)) {
        setState(await platform.enterFullscreen());
      }
    })();
  }, [platform]);

  // Inset all four sides so app content stays clear of the device safe area (notch,
  // home indicator) and Discord's own mobile UI. On Discord activities the
  // authoritative source is CSS env(safe-area-inset-*) (requires viewport-fit=cover);
  // this SDK version has no JS safe-area command. The JS insets from
  // enterFullscreen() are folded in via max() when present (and default to 0 / no-op
  // on desktop and web), so neither source is double-counted.
  const jsInset = (side: 'top' | 'right' | 'bottom' | 'left') =>
    (state.safeAreaInsets?.[side] || 0) + (state.contentSafeAreaInsets?.[side] || 0);
  const pad = (side: 'top' | 'right' | 'bottom' | 'left') =>
    `max(env(safe-area-inset-${side}), ${jsInset(side)}px)`;

  // Keep the original absolute + w-full + height:calc structure (the whole app's
  // h-full chain depends on it). Only add: a black backing, and left/right insets
  // (notch + Discord's side controls). Top/bottom stay flush so the letterboxed
  // game viewport keeps full height.
  const topInset = jsInset('top');

  return (
    <ViewportContext.Provider value={state}>
      <div
        style={{
          marginTop: `${topInset}px`,
          height: `calc(100% - ${topInset}px)`,
          boxSizing: 'border-box',
          backgroundColor: '#000',
          paddingRight: pad('right'),
          paddingLeft: pad('left'),
        }}
        className="absolute w-full"
      >
        {children}
      </div>
    </ViewportContext.Provider>
  );
};
